import React, { useReducer, useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardSidebar from '../components/DashboardSidebar';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import { C } from '../album/albumColors';
import { TOTAL, base64ToStickers, countsFromBase64, ownedSetFromBase64, loadBulkCache, replaceBulkCache, getCachedUsers, buildTradeIndex, userColor, userInitial, stickerCode, buildBitmapFromCounts, bitmapAndToList } from '../album/albumData';
import { initialState, albumReducer } from '../album/albumReducer';
import AlbumActionBar from '../album/components/AlbumActionBar';
import OtherUserScheduleSummary from '../components/sedes/OtherUserScheduleSummary';
import AlbumGrid from '../album/components/AlbumGrid';
import MobileAlbumGrid from '../album/components/MobileAlbumLayouts';
import RightPanel from '../album/components/RightPanel';
import TradeSummaryBar from '../album/components/TradeSummaryBar';
import TradeContextBanner from '../album/components/TradeContextBanner';
import ValuationPicker from '../album/components/ValuationPicker';
import ConfirmDialog from '../album/components/ConfirmDialog';
import Toast from '../album/components/Toast';
import ExportAlbumDialog from '../album/components/ExportAlbumDialog';
import ImportAlbumDialog from '../album/components/ImportAlbumDialog';
import { scrollToSticker } from '../album/utils/smoothScroll';
import { ALBUM_VARIANT_NAMES, getAlbumTheme } from '../album/albumVariants';
import { CHROME_VARIANT_NAMES, getChromeVariant, CHROME_DEFAULT_IDX } from '../album/chromeVariants';
import { countryForSticker } from '../album/albumDecorations';
import { RIGHTBAR_VARIANT_NAMES } from '../album/components/AlbumTradesPanel';
import { DASHBOARD_VARIANT_NAMES } from '../album/components/Album2Dashboard';
import { albumService, communityService, userService } from '../services/api';
import { buildWhatsappLink, buildMailtoLink, buildTradeMessage } from '../album/utils/whatsapp';
import { useAuth } from '../hooks/useAuth';
import useIsMobile from '../album/hooks/useIsMobile';
import { useAlbumDashboard } from '../album/AlbumDashboardContext';
import { isDevMode } from '../utils/devMode';

// Cada layout tiene un labelKey que el componente traduce con t(); las que ya
// son nombres estables (ej. "Heatmap") se quedan en inglés.
const LAYOUT_OPTIONS = [
  { id: 'country-cards-5', labelKey: 'layout_country_cards_5' },
  { id: 'grid',            labelKey: 'layout_grid' },
  { id: 'accordion',       labelKey: 'layout_accordion' },
  { id: 'by-country',      labelKey: 'layout_by_country' },
  { id: 'country-roster',  labelKey: 'layout_country_roster' },
  { id: 'booklet',         labelKey: 'layout_booklet' },
];

const ALBUM_VARIANT_STORAGE_KEY = 'album.variant';
const ALBUM_LAYOUT_STORAGE_KEY = 'album.layout';
const ALBUM_RIGHTBAR_STORAGE_KEY = 'album.rightbar';
const ALBUM_DASHBOARD_STORAGE_KEY = 'album.dashboard';
const ALBUM_CHROME_STORAGE_KEY = 'album.chrome';

function readPersistedIndex(key, max) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return 0;
    const n = parseInt(raw, 10);
    if (Number.isFinite(n) && n >= 0 && n < max) return n;
  } catch (_) {}
  return 0;
}

// Layouts that look great at phone widths. The other LAYOUT_OPTIONS use
// hard-coded 12-50 column grids that collapse into unreadable strips on mobile.
const MOBILE_LAYOUT_IDS = new Set(['country-cards-5', 'country-roster']);

function StickerAlbum({ albumVariant }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, dispatch] = useReducer(albumReducer, initialState);
  const { user } = useAuth();
  const myUserId = user?.id ?? null;
  const myDisplayName = user?.name || '';
  const myPhone = user?.phone || null;
  const theme = getThemeVars(ACTIVE_THEME);
  const [barVariant, setBarVariant] = useState(() => readPersistedIndex(ALBUM_VARIANT_STORAGE_KEY, ALBUM_VARIANT_NAMES.length));
  const albumTheme = getAlbumTheme(barVariant);
  const [layoutIdx, setLayoutIdx] = useState(() => readPersistedIndex(ALBUM_LAYOUT_STORAGE_KEY, LAYOUT_OPTIONS.length));
  const [rightbarIdx, setRightbarIdx] = useState(() => readPersistedIndex(ALBUM_RIGHTBAR_STORAGE_KEY, RIGHTBAR_VARIANT_NAMES.length));
  const [dashboardIdx, setDashboardIdx] = useState(() => readPersistedIndex(ALBUM_DASHBOARD_STORAGE_KEY, DASHBOARD_VARIANT_NAMES.length));
  const [chromeIdx, setChromeIdx] = useState(() => readPersistedIndex(ALBUM_CHROME_STORAGE_KEY, CHROME_VARIANT_NAMES.length) || CHROME_DEFAULT_IDX);
  const chrome = useMemo(() => getChromeVariant(chromeIdx), [chromeIdx]);
  useEffect(() => { try { localStorage.setItem(ALBUM_CHROME_STORAGE_KEY, String(chromeIdx)); } catch (_) {} }, [chromeIdx]);
  const [showSelectors, setShowSelectors] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const isMobile = useIsMobile();
  const [confirm, setConfirm] = useState(null); // {type: 'complete'|'cancel', tradeId, busy?}
  const [toast, setToast] = useState(null);     // {message, variant, actionLabel?, onAction?}
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const scrollContainerRef = useRef(null);

  // Handler que la barra de búsqueda dispara al agregar una figurita: scroll
  // suave (easing cúbico) hasta la celda y un pulso dorado breve para
  // ubicarla con el ojo.
  const handleStickerAdded = useCallback((n) => {
    const el = scrollContainerRef.current;
    if (!el || n == null) return;
    // Esperamos un frame para que el DOM refleje cualquier reflow del chip
    // strip que aparece al pasar de selección 0 → 1.
    requestAnimationFrame(() => scrollToSticker(el, n, { duration: 600 }));
  }, []);

  // Handler para navegar a un país sin agregar figuritas — usado en /album/5
  // cuando el usuario tipea sólo país (ej. "arg" o "argentina") y presiona Enter.
  const handleNavigateToCountry = useCallback((firstStickerN) => {
    const el = scrollContainerRef.current;
    if (!el || firstStickerN == null) return;
    requestAnimationFrame(() =>
      scrollToSticker(el, firstStickerN, { duration: 600, block: 'start', pulse: false })
    );
  }, []);

  const availableLayouts = useMemo(
    () => isMobile ? LAYOUT_OPTIONS.filter(l => MOBILE_LAYOUT_IDS.has(l.id)) : LAYOUT_OPTIONS,
    [isMobile]
  );

  useEffect(() => {
    if (layoutIdx >= availableLayouts.length) setLayoutIdx(0);
  }, [availableLayouts.length, layoutIdx]);

  const currentLayout = availableLayouts[layoutIdx] ?? availableLayouts[0];
  const selectionMode = currentLayout?.selectionMode || 'chips-bar';

  useEffect(() => {
    try { localStorage.setItem(ALBUM_VARIANT_STORAGE_KEY, String(barVariant)); } catch (_) {}
  }, [barVariant]);

  useEffect(() => {
    try { localStorage.setItem(ALBUM_LAYOUT_STORAGE_KEY, String(layoutIdx)); } catch (_) {}
  }, [layoutIdx]);

  useEffect(() => {
    try { localStorage.setItem(ALBUM_RIGHTBAR_STORAGE_KEY, String(rightbarIdx)); } catch (_) {}
  }, [rightbarIdx]);

  useEffect(() => {
    try { localStorage.setItem(ALBUM_DASHBOARD_STORAGE_KEY, String(dashboardIdx)); } catch (_) {}
  }, [dashboardIdx]);

  // Mobile sólo tiene dos variantes ahora:
  //   /album    → mobile-cards-1   (un país por fila, layout default)
  //   /album/1  → mobile-cards-2-xtight (extra-compacta, más tamaño de figurita)
  // Ambas usan el header con QuickAddBar inline (heredado del legacy
  // /album/5): buscador instagram-style para agregar/navegar y botón
  // "Exportar progreso" al final del scroll.
  const VARIANT_CONFIG = {
    '1': { layoutId: 'mobile-cards-2-xtight', quickAdd: true },
  };
  const variantConfig = albumVariant
    ? VARIANT_CONFIG[albumVariant]
    : { layoutId: 'mobile-cards-1', quickAdd: true };
  const mobileLayoutId = variantConfig.layoutId;
  const mobileQuickAdd = !!variantConfig.quickAdd;

  // En /album, cuando el panel derecho se expande el album baja una columna
  // (de country-cards-5 a country-cards = 4 cards/row a desktop-xl). Tanto
  // dashboard como collectors usan el mismo ancho de panel y la misma
  // reducción del album, así son simétricos visualmente.
  const { expanded: dashboardExpanded } = useAlbumDashboard();
  const effectiveLayoutId = dashboardExpanded && currentLayout.id === 'country-cards-5'
    ? 'country-cards'
    : currentLayout.id;

  // Mantener el ancho de las cards de país INVARIANTE al expandir el panel
  // derecho. La idea es: medir el ancho de una card cuando el panel está
  // cerrado (mainRef → primer [data-album-card]), también detectar cuántas
  // columnas tiene el grid actualmente (vía gridTemplateColumns), y al
  // expandir fijar el ancho del <main> a (colCount-1) × colW + paddings +
  // gaps. El <aside> (RightPanel) usa flex-1 para tomar lo que sobre. Solo
  // aplica a desktop-xl: por debajo, country-cards y country-cards-5 muestran
  // las mismas cols (tablet-md:grid-cols-3, etc.) — no hace falta fijar nada.
  const mainRef = useRef(null);
  const [colW, setColW] = useState(null);
  const [colCount, setColCount] = useState(null);
  const [isDesktopXl, setIsDesktopXl] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth >= 1280
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setIsDesktopXl(window.innerWidth >= 1280);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => {
    if (!mainRef.current) return;
    let raf = 0;
    let attempts = 0;

    // Modo normal: leer width real del primer card.
    const measureNormal = () => {
      if (!mainRef.current) return;
      const card = mainRef.current.querySelector('[data-album-card]');
      if (!card) {
        if (attempts++ < 60) raf = requestAnimationFrame(measureNormal);
        return;
      }
      const w = card.getBoundingClientRect().width;
      if (w > 50) setColW(w);
      const grid = card.parentElement;
      if (grid) {
        const cs = window.getComputedStyle(grid);
        const cols = cs.gridTemplateColumns.split(' ').filter(Boolean).length;
        if (cols > 0) setColCount(cols);
      }
    };

    // Modo expandido: el card real está achicado por el width fijo del
    // <main>, así que no podemos medirlo directo. Calculamos cuál sería el
    // colW si el panel estuviera cerrado: viewport menos sidebar izquierdo
    // (medido vía mainRef.left) menos sidebar derecho default (18rem).
    // Para el nº de cols normal usamos el cols actual del grid expandido + 1
    // (porque country-cards muestra siempre 1 col menos que country-cards-5
    // en cualquier breakpoint donde aplica el JS-fix, ≥1280).
    const measureExpanded = () => {
      if (!mainRef.current) return;
      if (window.innerWidth < 1280) return;
      const card = mainRef.current.querySelector('[data-album-card]');
      if (!card) {
        if (attempts++ < 60) raf = requestAnimationFrame(measureExpanded);
        return;
      }
      const grid = card.parentElement;
      if (!grid) return;
      const cs = window.getComputedStyle(grid);
      const expCols = cs.gridTemplateColumns.split(' ').filter(Boolean).length;
      if (expCols < 2) return;
      const normalCols = expCols + 1;
      const sidebarLeftW = mainRef.current.getBoundingClientRect().left;
      const idealAlbumW = window.innerWidth - sidebarLeftW - 288; // 18rem
      if (idealAlbumW < 100) return;
      const newColW = (idealAlbumW - 24 - (normalCols - 1) * 12) / normalCols;
      if (newColW > 50) {
        setColW(newColW);
        setColCount(normalCols);
      }
    };

    const measure = dashboardExpanded ? measureExpanded : measureNormal;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(mainRef.current);
    // Cuando expanded, el ResizeObserver de main no dispara al resize del
    // viewport (main width está fijado por JS y no cambia). Necesitamos
    // un listener directo de window para remedir.
    window.addEventListener('resize', measure);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [dashboardExpanded, effectiveLayoutId]);

  // country-cards usa px-3 (24 total) y gap-3 (12). Total = (cols-1) × colW
  // + 24 padding + (cols-2) × 12 gaps. Solo aplico cuando vamos de N→N-1,
  // que es la transición real a desktop-xl (5↔4 a desktop-xxl, 4↔3 a
  // desktop-xl) — por debajo el grid no cambia nº de cols.
  const expandedCols = colCount && colCount > 1 ? colCount - 1 : null;
  const mainStyleExpanded = dashboardExpanded && colW && expandedCols && isDesktopXl
    ? { width: `${expandedCols * colW + 24 + (expandedCols - 1) * 12}px`, flex: '0 0 auto' }
    : null;

  // ── Backend state ──────────────────────────────────────────────────
  const [myBase64, setMyBase64] = useState(null);       // base64 string from backend (or null)
  const [bulkCache, setBulkCache] = useState(() => loadBulkCache());
  const [incomingTrades, setIncomingTrades] = useState([]);
  const [outgoingTrades, setOutgoingTrades] = useState([]);
  const [activeTrades, setActiveTrades] = useState([]);
  const [historyTrades, setHistoryTrades] = useState([]);
  const [myLocks, setMyLocks] = useState([]); // [{stickerNum, lockedCount}, ...]
  const [myValuations, setMyValuations] = useState({}); // {stickerNum: 1..5}
  const [saving, setSaving] = useState(false);
  // Context banner state (driven by ?tradeId / ?offerId URL params)
  const [contextTrade, setContextTrade] = useState(null); // mapped trade for banner
  const [contextOffer, setContextOffer] = useState(null); // offer object for banner
  const [loading, setLoading] = useState(true);
  const [valuationTarget, setValuationTarget] = useState(null); // sticker number being valued

  // ── Performance test: cargar albums mock desde /test-albums.json ────
  useEffect(() => {
    window.__albumLoadTestData = async () => {
      console.time('fetch test-albums.json');
      const res = await fetch('/test-albums.json');
      const data = await res.json();
      console.timeEnd('fetch test-albums.json');
      setBulkCache(data);
    };
    return () => { delete window.__albumLoadTestData; };
  }, []);

  // ── Fetch my album on mount ────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await albumService.getMyAlbum();
        const album = res.data;
        if (!cancelled) setMyBase64(album?.data ?? null);
      } catch (e) {
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Bulk download (background) ─────────────────────────────────────
  // El endpoint /album/bulk devuelve siempre la lista canónica completa de
  // coleccionistas alcanzables (que comparten comunidad con el caller). Cada
  // sync reemplaza el cache entero — así no quedan entradas stale de gente
  // con la que dejamos de compartir comunidad.
  const lastBulkFetch = React.useRef(0);
  const BULK_MIN_INTERVAL_MS = 12 * 60 * 1000; // mínimo 12 min entre pedidos

  const syncBulk = useCallback(async () => {
    if (Date.now() - lastBulkFetch.current < BULK_MIN_INTERVAL_MS) return;
    lastBulkFetch.current = Date.now();
    try {
      const res = await albumService.getBulkAlbums();
      const bulk = res.data;
      if (bulk) {
        const updated = replaceBulkCache(bulk.serverTime, bulk.albums || []);
        setBulkCache({ ...updated });
      }
    } catch (e) {
    }
  }, []);

  // Al montar + al volver a la pestaña (con mínimo 5 min entre pedidos)
  useEffect(() => {
    syncBulk();
    const onVisibility = () => { if (document.visibilityState === 'visible') syncBulk(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { document.removeEventListener('visibilitychange', onVisibility); };
  }, [syncBulk]);

  // ── Fetch trades + locks ───────────────────────────────────────────
  // Tras un fetch exitoso disparamos `album:tradesChanged` para que las
  // otras instancias de useAlbumTrades (ej. la del AlbumTradesPanel del
  // sidebar) refresquen sin esperar a su poll de 30s.
  const refreshTrades = useCallback(async () => {
    try {
      const [inRes, outRes, activeRes, historyRes, locksRes] = await Promise.all([
        albumService.getIncomingTrades(),
        albumService.getOutgoingTrades(),
        albumService.getActiveTrades(),
        albumService.getTradeHistory(),
        albumService.getMyLocks(),
      ]);
      setIncomingTrades(Array.isArray(inRes.data) ? inRes.data : []);
      setOutgoingTrades(Array.isArray(outRes.data) ? outRes.data : []);
      setActiveTrades(Array.isArray(activeRes.data) ? activeRes.data : []);
      setHistoryTrades(Array.isArray(historyRes.data) ? historyRes.data : []);
      setMyLocks(Array.isArray(locksRes.data) ? locksRes.data : []);
      window.dispatchEvent(new CustomEvent('album:tradesChanged'));
    } catch (e) {
      // silencioso: el panel se mostrará con lo que ya teníamos
    }
  }, []);

  useEffect(() => { refreshTrades(); }, [refreshTrades]);

  // (URL param context effect lives below the mappedActive/Incoming/Outgoing
  // useMemo declarations to avoid a TDZ at module init.)

  // Mirror in-album dispatches (right-panel cards calling VIEW_TRADE_DETAIL /
  // VIEW_TRADE_REVIEW) into contextTrade so the banner appears for those too.
  // Skip when ?tradeId/?offerId is set — that path already drives contextTrade.
  useEffect(() => {
    if (searchParams.get('tradeId') || searchParams.get('offerId')) return;
    if (!state.activeTrade) {
      if (contextTrade && (state.viewMode === 'MY_ALBUM' || state.viewMode === 'USER_ALBUM')) {
        setContextTrade(null);
      }
      return;
    }
    const status = state.viewMode === 'TRADE_DETAIL' ? 'ACCEPTED'
                 : state.viewMode === 'TRADE_REVIEW' ? 'PENDING'
                 : state.viewMode === 'OFFER_REVIEW' ? (state.activeTrade?.status || 'OPEN')
                 : null;
    if (!status) return;
    setContextTrade(prev => {
      if (prev && prev.id === state.activeTrade.id && prev.status === status) return prev;
      return { ...state.activeTrade, status };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.activeTrade, state.viewMode, searchParams]);

  // Detectar nuevos incoming PENDING o nuevos ACCEPTED para notificar al
  // usuario sin que tenga que clickear cada card. Sólo dispara en
  // refreshes posteriores al primero (no en mount).
  const lastTradeIdsRef = React.useRef(null);
  useEffect(() => {
    const inIds = new Set(incomingTrades.map(t => t.id));
    const acIds = new Set(activeTrades.map(t => t.id));
    const prev = lastTradeIdsRef.current;
    if (prev) {
      const newIncoming = [...inIds].filter(id => !prev.in.has(id));
      // Trades que aparecieron en active y no estaban antes.
      const newlyActive = [...acIds].filter(id => !prev.ac.has(id));
      // De ésos, los que vinieron de mi outgoing son los aceptados por la
      // otra parte (los que vienen de incoming son los que YO acepté → no
      // disparan notificación porque el toast ya lo mostró handleAcceptTrade).
      const fromMyOutgoing = newlyActive.filter(id => prev.out.has(id));

      if (newIncoming.length === 1) {
        const t0 = incomingTrades.find(x => x.id === newIncoming[0]);
        const name = bulkCache?.albums?.[t0?.proposerId]?.n || '';
        setToast({ message: t('Nueva propuesta de {{name}}', { name }), variant: 'info' });
      } else if (newIncoming.length > 1) {
        setToast({ message: t('{{n}} propuestas nuevas', { n: newIncoming.length }), variant: 'info' });
      }
      if (fromMyOutgoing.length > 0) {
        setToast({
          message: t('Tu propuesta fue aceptada — coordinen por WhatsApp'),
          variant: 'success',
        });
      }
    }
    lastTradeIdsRef.current = {
      in: inIds,
      ac: acIds,
      out: new Set(outgoingTrades.map(t => t.id)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingTrades, activeTrades, outgoingTrades]);

  // Polling cada 30s para detectar cambios del otro lado (ej. mi outgoing
  // pasó a ACCEPTED porque el receiver aceptó). Si la pestaña no está
  // visible no pollea (chrome ahora lo throttlea igual, pero somos explícitos).
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') refreshTrades();
    }, 30000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshTrades();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshTrades]);

  // ── Fetch my valuations once ───────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    albumService.getMyValuations()
      .then(res => { if (!cancelled) setMyValuations(res.data || {}); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  const handleSetValuation = useCallback(async (stickerNum, value) => {
    const optimistic = { ...myValuations };
    if (!value || value <= 1) delete optimistic[stickerNum];
    else optimistic[stickerNum] = value;
    setMyValuations(optimistic);
    setValuationTarget(null);
    try {
      const res = await albumService.setMyValuations({ [String(stickerNum)]: value });
      setMyValuations(res.data || {});
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.data?.message || t('No se pudo guardar la valuación');
      setToast({ message: msg, variant: 'error' });
      // revert: re-fetch authoritative state
      try {
        const fresh = await albumService.getMyValuations();
        setMyValuations(fresh.data || {});
      } catch (_) { /* ignore */ }
    }
  }, [myValuations, t]);

  // ── Derived data ───────────────────────────────────────────────────
  const myStickers = useMemo(() => base64ToStickers(myBase64), [myBase64]);
  const ownedCount = useMemo(() => myStickers.filter(s => s.owned).length, [myStickers]);
  const totalReps = useMemo(
    () => myStickers.reduce((acc, s) => acc + Math.max(0, (s.reps || 0)), 0),
    [myStickers]
  );
  const [repesTipDismissed, setRepesTipDismissed] = useState(() => {
    try { return localStorage.getItem('album.tip.repes.dismissed') === '1'; } catch { return false; }
  });
  const dismissRepesTip = useCallback(() => {
    try { localStorage.setItem('album.tip.repes.dismissed', '1'); } catch {}
    setRepesTipDismissed(true);
  }, []);
  const showRepesTip = ownedCount > 30 && totalReps === 0 && !repesTipDismissed;
  const myOwnedSet = useMemo(() => new Set(myStickers.filter(s => s.owned).map(s => s.n)), [myStickers]);
  const mySpareSet = useMemo(() => new Set(myStickers.filter(s => (s.reps || 0) >= 2).map(s => s.n)), [myStickers]);
  const myCounts = useMemo(() => countsFromBase64(myBase64), [myBase64]);

  const theirOwnedSet = useMemo(
    () => new Set(state.theirStickers.filter(s => s.owned).map(s => s.n)),
    [state.theirStickers]
  );
  const theirSpareSet = useMemo(
    () => new Set(state.theirStickers.filter(s => (s.reps || 0) >= 2).map(s => s.n)),
    [state.theirStickers]
  );

  // ── Community filter — backend-driven ────────────────────────────────
  // Cuando hay comunidades seleccionadas pegamos /album/bulk?communityIds=…
  // y reemplazamos el cache visible con la respuesta filtrada. Cuando no
  // hay filtro, usamos el bulkCache completo de localStorage.
  const COMMUNITY_FILTER_KEY = 'cm_album_community_filter';
  const [myCommunities, setMyCommunities] = useState([]);
  const [selectedCommunityIds, setSelectedCommunityIds] = useState(() => {
    try {
      const raw = localStorage.getItem(COMMUNITY_FILTER_KEY);
      if (!raw) return [];
      return raw.split(',').map(s => parseInt(s, 10)).filter(n => Number.isFinite(n) && n > 0);
    } catch { return []; }
  });
  const [filteredBulk, setFilteredBulk] = useState(null); // { albums: { [uid]: { n, d, t } } } o null

  // Single-select: 0 limpia; mismo id desactiva; otro id reemplaza.
  const toggleCommunity = useCallback((id) => {
    setSelectedCommunityIds(prev => {
      if (id === 0) return [];
      if (prev.length === 1 && prev[0] === id) return [];
      return [id];
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    communityService.getUserCommunities()
      .then(res => {
        if (cancelled) return;
        const list = Array.isArray(res?.data?.data) ? res.data.data : (Array.isArray(res?.data) ? res.data : []);
        setMyCommunities(list);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Persist filter selection.
  useEffect(() => {
    try { localStorage.setItem(COMMUNITY_FILTER_KEY, selectedCommunityIds.join(',')); } catch {}
  }, [selectedCommunityIds]);

  // Filtrar localmente el bulkCache por comunidades — sin pedir nada al
  // backend. Cada entry ya viene con `c` (lista de comunidades compartidas
  // con el caller), así que basta con quedarse con las que intersectan
  // `selectedCommunityIds`.
  useEffect(() => {
    if (selectedCommunityIds.length === 0) {
      setFilteredBulk(null);
      return;
    }
    if (!bulkCache?.albums) return;
    const wanted = new Set(selectedCommunityIds);
    const albums = {};
    for (const [uid, entry] of Object.entries(bulkCache.albums)) {
      const cids = Array.isArray(entry.c) ? entry.c : [];
      let shared = false;
      for (const cid of cids) {
        if (wanted.has(cid)) { shared = true; break; }
      }
      if (shared) albums[uid] = entry;
    }
    setFilteredBulk({ serverTime: bulkCache.serverTime || null, albums });
  }, [selectedCommunityIds, bulkCache]);

  // El filtro por myCommunityIdSet purga defensivamente cualquier entrada del
  // cache cuyas comunidades ya no se solapen con las del usuario (típicamente
  // porque dejó la comunidad). Si todavía no cargaron las comunidades del
  // usuario, no filtramos para no esconder usuarios mientras se hidratan.
  const myCommunityIdSet = useMemo(() => {
    if (!Array.isArray(myCommunities) || myCommunities.length === 0) return null;
    return new Set(myCommunities.map(c => c.id));
  }, [myCommunities]);
  const allCachedUsers = useMemo(
    () => getCachedUsers(bulkCache, myCommunityIdSet),
    [bulkCache, myCommunityIdSet]
  );

  const effectiveBulk = filteredBulk || bulkCache;
  const allUsers = useMemo(() => {
    if (filteredBulk) return getCachedUsers(filteredBulk, myCommunityIdSet);
    return allCachedUsers;
  }, [filteredBulk, allCachedUsers, myCommunityIdSet]);
  // Precomputed bitmap index — se calcula sobre el bulk efectivo (filtrado
  // o completo) así matches/búsquedas/Ofertas respetan el filtro activo.
  const tradeIndex = useMemo(() => {
    console.time('buildTradeIndex');
    const idx = buildTradeIndex(effectiveBulk);
    console.timeEnd('buildTradeIndex');
    return idx;
  }, [effectiveBulk]);


  // ── View a user's album (from cache or fetch) ──────────────────────
  // Acepta:
  //  - pendingSelection: si viene desde SelectionMatchCard / SmartTradeCard "Armar
  //    propuesta", la selección original del MY_ALBUM se splittea en offering/
  //    requesting cuando se entra a TRADE_MATCH (vía useEffect más abajo).
  //  - autoEnterMatch: si es true, al entrar saltea USER_ALBUM y va directamente
  //    a TRADE_MATCH (caso típico: tocar a alguien en SelectionMatchCard).
  // Schedules del usuario actualmente mirado, indexado por userId. Se popula
  // en el response de getUserAlbum (ver más abajo). Se mantiene fuera del
  // cache para no inflar la persistencia y porque es info "viva".
  const [userSchedulesByUser, setUserSchedulesByUser] = useState({});

  const viewUserAlbum = useCallback((user, pendingSelection = null, { autoEnterMatch = false } = {}) => {
    const cached = bulkCache?.albums?.[user.id];
    const stickers = base64ToStickers(cached?.d ?? null);
    // Default a country-cards-5 al entrar al álbum de otro user.
    setLayoutIdx(0);
    // Mostrar inmediatamente con datos del cache.
    dispatch({ type: 'VIEW_USER_ALBUM', user, stickers, pendingSelection });
    // Si el flujo es directo a match (ej. desde SelectionMatchCard), pasamos a
    // TRADE_MATCH en el siguiente tick — React 18 batchea, así el reducer
    // primero resuelve VIEW_USER_ALBUM (con pendingSelection) y luego
    // VIEW_TRADE_MATCH disparará el useEffect que aplica la selección.
    if (autoEnterMatch) {
      dispatch({ type: 'VIEW_TRADE_MATCH' });
    }

    // Background: verificar con el backend si el album cambió (comentar este bloque para no hacer la query)
    // No mandamos If-Modified-Since para asegurar que vengan los userSchedules
    // en cada apertura — el server omite el header lastModified cuando hay
    // userSchedules, así que un 304 jamás viene si los hay.
    const headers = {};
    if (cached?.t) headers['If-Modified-Since'] = new Date(cached.t).toUTCString();
    albumService.getUserAlbum(user.id, headers).then(res => {
      if (res.status === 304) return; // no cambió, el cache está al día
      const album = res.data;
      if (album?.data && album.data !== cached?.d) {
        // Actualizar cache local
        setBulkCache(prev => {
          const updated = { ...prev, albums: { ...prev.albums, [user.id]: { ...prev.albums[user.id], d: album.data, t: album.updatedAt } } };
          return updated;
        });
        // Actualizar la vista si seguimos mirando este usuario
        dispatch({ type: 'REFRESH_THEIR_STICKERS', stickers: base64ToStickers(album.data) });
      }
      // Schedules del owner del álbum en comunidades compartidas. El server
      // omite el campo si es vacío o si soy yo mismo el owner.
      if (Array.isArray(album?.userSchedules)) {
        setUserSchedulesByUser(prev => ({ ...prev, [user.id]: album.userSchedules }));
      }
    }).catch(() => {}); // silencioso — el cache ya se mostró
  }, [bulkCache]);

  // ── Deep-link a USER_ALBUM via ?userId=X ──────────────────────────
  // Si la URL trae userId al cargar (typically navegando desde la card dorada
  // del perfil del user, o desde el Album Dashboard), abrir directamente el
  // álbum de ese user. La AlbumActionBar provee el botón "← Mi álbum" para
  // volver. El query param se limpia tras el dispatch para no quedar pegado
  // en navegaciones siguientes.
  const userIdParam = searchParams.get('userId');
  useEffect(() => {
    if (!userIdParam) return;
    const targetId = parseInt(userIdParam, 10);
    if (Number.isNaN(targetId)) return;
    if (myUserId && targetId === myUserId) {
      // Es el propio user — quedarse en MY_ALBUM y limpiar el param.
      const next = new URLSearchParams(searchParams);
      next.delete('userId');
      setSearchParams(next, { replace: true });
      return;
    }

    let cancelled = false;
    const cached = bulkCache?.albums?.[targetId];
    const buildUser = (data) => ({
      id: targetId,
      name: data?.name || cached?.n || `User ${targetId}`,
      initial: ((data?.name || cached?.n || '?').charAt(0) || '?').toUpperCase(),
      color: userColor(targetId),
    });

    (async () => {
      let userData = null;
      try {
        const r = await userService.getUserById(targetId);
        userData = r?.data ?? null;
      } catch { /* fallback al cache si no podemos resolver el user */ }
      if (cancelled) return;
      viewUserAlbum(buildUser(userData));
      // Limpiar el query param tras abrir el USER_ALBUM. Usar replace para
      // que el back del browser no vuelva a este state intermedio.
      const next = new URLSearchParams(searchParams);
      next.delete('userId');
      setSearchParams(next, { replace: true });
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdParam, myUserId]);

  // Cuando entramos a TRADE_MATCH desde "Contraofertar" en una propuesta
  // previa, guardamos el id de esa propuesta acá. Al enviar la contraoferta
  // (handleProposeTrade) primero cancelamos esta y después creamos la nueva.
  // Si el user abandona el flujo, la original queda intacta.
  const [pendingCounterOf, setPendingCounterOf] = useState(null);

  // ── Listener para abrir TRADE_MATCH desde fuera (ej: contraofertar) ─
  useEffect(() => {
    const onOpen = (e) => {
      const { userId, originalTradeId } = e.detail || {};
      if (!userId) return;
      const cached = bulkCache?.albums?.[userId];
      if (!cached) return;
      const user = {
        id: userId,
        name: cached.n,
        initial: (cached.n || '?').charAt(0).toUpperCase(),
        color: '#10b981',
      };
      setPendingCounterOf(originalTradeId || null);
      viewUserAlbum(user, null, { autoEnterMatch: true });
    };
    window.addEventListener('album:openTradeMatch', onOpen);
    return () => window.removeEventListener('album:openTradeMatch', onOpen);
  }, [bulkCache, viewUserAlbum]);

  // Si el user sale del flujo (vuelve a su álbum o entra al de otro user
  // distinto), limpiamos pendingCounterOf así una propuesta nueva sin
  // contexto de contraoferta no rejecta nada por accidente.
  useEffect(() => {
    if (state.viewMode === 'MY_ALBUM' && pendingCounterOf != null) {
      setPendingCounterOf(null);
    }
  }, [state.viewMode, pendingCounterOf]);

  // ── Build a "user" object for trade display ────────────────────────
  const tradeUserFromId = useCallback((userId) => {
    const cached = bulkCache?.albums?.[userId];
    return {
      id: userId,
      name: cached?.n || `User ${userId}`,
      initial: userInitial(cached?.n),
      color: userColor(userId),
      owned: 0,
    };
  }, [bulkCache]);

  // Map incoming trades to the shape the UI expects.
  // Para que la grilla y el bar de TRADE_REVIEW pinten desde MI perspectiva
  // sin importar quién propuso, calculamos `youGive` y `youReceive` siempre
  // desde el ojo del usuario que mira el trade.
  const mappedIncoming = useMemo(() =>
    incomingTrades.map(t => ({
      id: t.id,
      direction: 'incoming',
      fromUser: tradeUserFromId(t.proposerId),
      otherUser: tradeUserFromId(t.proposerId),
      // Yo soy el receiver: lo que el proposer ofrece es lo que yo recibo,
      // y lo que el proposer pide es lo que yo doy.
      youGive: t.requested || [],
      youReceive: t.offered || [],
      stickersOffered: t.offered || [],
      stickersRequested: t.requested || [],
      createdAt: t.createdAt,
    })),
    [incomingTrades, tradeUserFromId]
  );

  // Outgoing: yo soy proposer, el otro es el receiver.
  const mappedOutgoing = useMemo(() =>
    outgoingTrades.map(t => ({
      id: t.id,
      direction: 'outgoing',
      otherUser: tradeUserFromId(t.receiverId),
      // Yo soy el proposer: ofrecí (offered) son las mías que doy; las que pedí (requested)
      // son las que recibiría si el otro acepta.
      youGive: t.offered || [],
      youReceive: t.requested || [],
      stickersOffered: t.offered || [],
      stickersRequested: t.requested || [],
      createdAt: t.createdAt,
    })),
    [outgoingTrades, tradeUserFromId]
  );

  // Active reservations: figuritas reservadas para completar.
  const mappedActive = useMemo(() => {
    if (myUserId === null) return [];
    return activeTrades.map(t => {
      const iAmProposer = t.proposerId === myUserId;
      const otherUserId = iAmProposer ? t.receiverId : t.proposerId;
      const youGive = iAmProposer ? (t.offered || []) : (t.requested || []);
      const youReceive = iAmProposer ? (t.requested || []) : (t.offered || []);
      return {
        id: t.id,
        otherUser: tradeUserFromId(otherUserId),
        youGive,
        youReceive,
        createdAt: t.createdAt,
        resolvedAt: t.resolvedAt,
      };
    });
  }, [activeTrades, tradeUserFromId, myUserId]);

  const mappedHistory = useMemo(() => {
    if (myUserId === null) return [];
    return historyTrades.map(t => {
      const iAmProposer = t.proposerId === myUserId;
      const otherUserId = iAmProposer ? t.receiverId : t.proposerId;
      return {
        id: t.id,
        direction: iAmProposer ? 'outgoing' : 'incoming',
        otherUser: tradeUserFromId(otherUserId),
        youGive: iAmProposer ? (t.offered || []) : (t.requested || []),
        youReceive: iAmProposer ? (t.requested || []) : (t.offered || []),
        stickersOffered: t.offered || [],
        stickersRequested: t.requested || [],
        status: t.status || 'COMPLETED',
        createdAt: t.createdAt,
        resolvedAt: t.resolvedAt,
        completedAt: t.completedAt,
      };
    });
  }, [historyTrades, tradeUserFromId, myUserId]);

  // ── URL param context: ?tradeId / ?offerId ─────────────────────────
  // Resolve the trade/offer from URL params and set up context banner.
  // Runs whenever searchParams or the trade lists change (trades may load
  // after the URL param is first read).
  useEffect(() => {
    const tradeIdParam = searchParams.get('tradeId');
    const offerIdParam = searchParams.get('offerId');

    if (!tradeIdParam && !offerIdParam) {
      setContextTrade(null);
      setContextOffer(null);
      return;
    }

    if (tradeIdParam) {
      const tid = parseInt(tradeIdParam, 10);
      if (isNaN(tid)) return;

      const fromActive = mappedActive.find(t => t.id === tid);
      if (fromActive) {
        setContextTrade({ ...fromActive, status: 'ACCEPTED' });
        dispatch({ type: 'VIEW_TRADE_DETAIL', trade: { ...fromActive, status: 'ACCEPTED' } });
        setContextOffer(null);
        return;
      }
      const fromIncoming = mappedIncoming.find(t => t.id === tid);
      if (fromIncoming) {
        setContextTrade({ ...fromIncoming, status: 'PENDING' });
        dispatch({ type: 'VIEW_TRADE_REVIEW', trade: { ...fromIncoming, status: 'PENDING' } });
        setContextOffer(null);
        return;
      }
      const fromOutgoing = mappedOutgoing.find(t => t.id === tid);
      if (fromOutgoing) {
        setContextTrade({ ...fromOutgoing, status: 'PENDING' });
        dispatch({ type: 'VIEW_TRADE_REVIEW', trade: { ...fromOutgoing, status: 'PENDING' } });
        setContextOffer(null);
        return;
      }
      const fromHistory = mappedHistory.find(t => t.id === tid);
      if (fromHistory) {
        setContextTrade(fromHistory);
        dispatch({ type: 'VIEW_TRADE_DETAIL', trade: fromHistory });
        setContextOffer(null);
        return;
      }
      // Not found yet — wait for the lists to populate, the effect re-runs.
    }

    if (offerIdParam) {
      const oid = parseInt(offerIdParam, 10);
      if (isNaN(oid)) return;
      if (contextOffer?.id !== oid) {
        albumService.getOffer(oid)
          .then(res => {
            const offer = res.data?.data ?? res.data;
            if (offer && offer.id) {
              setContextOffer(offer);
              // Pintar las cartas en el grid igual que en una review.
              // Desde MI perspectiva: lo que el publicador OFRECE es lo que YO recibiría;
              // lo que PIDE es lo que YO daría. Excepción: si la oferta es mía, mantenemos
              // la semántica original (offered=youGive, requested=youReceive).
              const isMine = offer.userId === myUserId;
              dispatch({
                type: 'VIEW_OFFER_REVIEW',
                offer: isMine
                  ? offer
                  : { ...offer, offered: offer.requested, requested: offer.offered },
              });
            }
          })
          .catch(() => {});
      }
      setContextTrade(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, mappedActive, mappedIncoming, mappedOutgoing, mappedHistory, myUserId]);

  // Cuando el user "vuelve" desde TRADE_REVIEW/TRADE_DETAIL/OFFER_REVIEW al
  // MY_ALBUM, limpiar el ?tradeId/?offerId de la URL. Sin esto la URL queda
  // sucia y el siguiente click al banner navega al mismo URL — react-router
  // lo trata como no-op y el useEffect de arriba no re-dispatcha el view.
  // Usamos un ref para detectar la transición y NO disparar la limpieza en
  // el caso "carga inicial con tradeId" (donde viewMode empieza en MY_ALBUM).
  const prevViewModeRef = useRef(state.viewMode);
  useEffect(() => {
    const prev = prevViewModeRef.current;
    const wasInTradeContext =
      prev === 'TRADE_REVIEW' || prev === 'TRADE_DETAIL' || prev === 'OFFER_REVIEW';
    if (
      state.viewMode === 'MY_ALBUM' &&
      wasInTradeContext &&
      (searchParams.get('tradeId') || searchParams.get('offerId'))
    ) {
      setSearchParams({}, { replace: true });
    }
    prevViewModeRef.current = state.viewMode;
  }, [state.viewMode, searchParams, setSearchParams]);

  // Set de stickers reservados por mí — para badges en la grilla.
  const lockedStickerMap = useMemo(() => {
    const m = new Map();
    for (const lock of myLocks) m.set(lock.stickerNum, lock.lockedCount);
    return m;
  }, [myLocks]);

  // Figuritas que VOY A RECIBIR en trades ACCEPTED pero todavía no completados.
  // Map<stickerNum, Array<{ tradeId, fromUserId, fromUserName }>>.
  // Las usamos para (a) marcarlas en la grilla con opacidad y badge, y
  // (b) excluirlas de "myNeeds" cuando se calculan futuros matches.
  const incomingStickerMap = useMemo(() => {
    const m = new Map();
    for (const trade of mappedActive) {
      const source = {
        tradeId: trade.id,
        fromUserId: trade.otherUser?.id,
        fromUserName: trade.otherUser?.name || '',
      };
      for (const n of trade.youReceive || []) {
        const arr = m.get(n) || [];
        arr.push(source);
        m.set(n, arr);
      }
    }
    return m;
  }, [mappedActive]);

  // Continuidad de selección: cuando el usuario llega a TRADE_MATCH desde un
  // SelectionMatchCard, splittear la selección original en offering / requesting.
  // Espera a que theirStickers haya cargado para no descartar pendingSelection
  // antes de poder mapear las "requesting" (las que ELLOS tienen y yo no).
  useEffect(() => {
    if (state.viewMode !== 'TRADE_MATCH') return;
    if (!state.pendingSelection || state.pendingSelection.size === 0) return;
    if (!state.theirStickers || state.theirStickers.length === 0) return;
    for (const n of state.pendingSelection) {
      const iOwn = myOwnedSet.has(n);
      const theyOwn = theirOwnedSet.has(n);
      if (iOwn && !theyOwn && !state.tradeBuilder.offering.has(n)) {
        dispatch({ type: 'TOGGLE_TRADE_OFFER', n });
      } else if (!iOwn && theyOwn && !state.tradeBuilder.requesting.has(n)) {
        dispatch({ type: 'TOGGLE_TRADE_REQUEST', n });
      }
      // Las que ambos tienen o ninguno tiene se ignoran (no aplican al match).
    }
    dispatch({ type: 'CLEAR_PENDING_SELECTION' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.viewMode, state.pendingSelection, state.theirStickers]);

  // ── Grid logic ─────────────────────────────────────────────────────
  const gridStickers = state.viewMode === 'USER_ALBUM' ? state.theirStickers
    : state.viewMode === 'TRADE_MATCH' || state.viewMode === 'TRADE_REVIEW' ? myStickers
    : state.viewMode === 'OFFER_BUILDER' ? myStickers
    : state.viewMode === 'OFFER_REVIEW' ? myStickers
    : myStickers;

  // En modos de revisión / detalle de trade, recortamos la grilla a los
  // países que tienen al menos una figurita participante. En TRADE_MATCH
  // (constructor) recortamos a los que tienen swap viable: yo tengo extras
  // y ellos no, o ellos tienen extras y yo no. Lo demás es ruido visual.
  const participatingStickerSet = useMemo(() => {
    const collect = (arrA, arrB) => {
      const set = new Set();
      for (const n of (arrA || [])) set.add(n);
      for (const n of (arrB || [])) set.add(n);
      return set.size > 0 ? set : null;
    };
    if (state.viewMode === 'TRADE_REVIEW' && state.activeTrade) {
      return collect(state.activeTrade.youGive ?? state.activeTrade.stickersOffered,
                     state.activeTrade.youReceive ?? state.activeTrade.stickersRequested);
    }
    if (state.viewMode === 'TRADE_DETAIL' && state.activeTrade) {
      return collect(state.activeTrade.youGive, state.activeTrade.youReceive);
    }
    if (state.viewMode === 'OFFER_REVIEW' && state.activeTrade) {
      return collect(state.activeTrade.youGive, state.activeTrade.youReceive);
    }
    if (contextOffer) {
      return collect(contextOffer.offered, contextOffer.requested);
    }
    if (state.viewMode === 'TRADE_MATCH' && state.theirStickers && state.theirStickers.length) {
      const set = new Set();
      const theirByN = new Map(state.theirStickers.map(s => [s.n, s]));
      for (const s of myStickers) {
        const them = theirByN.get(s.n);
        if (!them) continue;
        const myFree = (s.reps || 0) - (lockedStickerMap.get(s.n) || 0);
        const iCanGive  = myFree >= 2 && !them.owned;
        const theyCanGive = (them.reps || 0) >= 2 && !s.owned;
        if (iCanGive || theyCanGive) set.add(s.n);
      }
      return set.size > 0 ? set : null;
    }
    if (state.viewMode === 'OFFER_BUILDER') {
      const set = new Set();
      for (const s of myStickers) {
        const myFree = (s.reps || 0) - (lockedStickerMap.get(s.n) || 0);
        if (myFree >= 2 || !s.owned) set.add(s.n);
      }
      return set.size > 0 ? set : null;
    }
    return null;
  }, [state.viewMode, state.activeTrade, contextOffer, state.theirStickers, myStickers, lockedStickerMap]);

  const valuationMap = useMemo(() => {
    const m = new Map();
    Object.entries(myValuations || {}).forEach(([k, v]) => {
      const n = parseInt(k, 10);
      if (!Number.isNaN(n) && v > 1) m.set(n, v);
    });
    return m;
  }, [myValuations]);

  const onValuate = useCallback((n) => setValuationTarget(n), []);
  const extras = {
    myOwnedSet,
    theirOwnedSet,
    mySpareSet,
    theirSpareSet,
    myCounts,
    activeTrade: state.activeTrade,
    tradeBuilder: state.tradeBuilder,
    lockedSet: lockedStickerMap,
    incomingSet: incomingStickerMap,
    valuationSet: valuationMap,
    onValuate,
  };

  const handleCellClick = async (sticker) => {
    if (state.viewMode === 'MY_ALBUM') {
      // On mobile, tap-to-add is the dominant interaction for two cases:
      //   1) sticker is missing → first copy
      //   2) sticker is reserved in an active trade → another copy on top of the locked one
      // Otherwise tapping an owned sticker toggles selection so multi-remove still works.
      const lockedHere = lockedStickerMap.get(sticker.n) || 0;
      const oneTapAdd = isMobile && (!sticker.owned || lockedHere > 0);
      if (oneTapAdd) {
        setSaving(true);
        try {
          if (!myBase64) {
            const empty = btoa(String.fromCharCode(...new Uint8Array(TOTAL)));
            await albumService.updateMyAlbum(empty);
          }
          const res = await albumService.addStickers([sticker.n]);
          setMyBase64(res.data?.data ?? null);
        } catch (e) {
          setToast({ message: t('No se pudo agregar la figurita'), variant: 'error' });
        } finally {
          setSaving(false);
        }
        return;
      }
      dispatch({ type: 'TOGGLE_SELECT', n: sticker.n });
    } else if (state.viewMode === 'TRADE_MATCH') {
      // Solo permitir agregar al trade lo que es realmente swappable: ellos
      // tienen repetida y yo no la tengo (pido), o yo tengo repetida y ellos
      // no la tienen (ofrezco).
      const theySpare = theirSpareSet.has(sticker.n);
      const iSpare = mySpareSet.has(sticker.n);
      const theyOwn = theirOwnedSet.has(sticker.n);
      const iOwn = myOwnedSet.has(sticker.n);
      if (theySpare && !iOwn) dispatch({ type: 'TOGGLE_TRADE_REQUEST', n: sticker.n });
      else if (iSpare && !theyOwn) dispatch({ type: 'TOGGLE_TRADE_OFFER', n: sticker.n });
    } else if (state.viewMode === 'OFFER_BUILDER') {
      const me = myStickers.find(s => s.n === sticker.n);
      if (!me) return;
      const lockedHere = lockedStickerMap.get(sticker.n) || 0;
      const myFree = (me.reps || 0) - lockedHere;
      if (myFree >= 2) dispatch({ type: 'TOGGLE_TRADE_OFFER', n: sticker.n });
      else if (!me.owned) dispatch({ type: 'TOGGLE_TRADE_REQUEST', n: sticker.n });
    }
  };

  // ── Add: +1 a cada figurita seleccionada (delta, no full album) ─────
  // Tras aplicar: la selección se vacía y la acción queda en el undo stack
  // (incluye la selección original para restaurarla con Deshacer).
  const handleSetOwned = useCallback(async () => {
    if (state.selected.size === 0) return;
    const nums = [...state.selected];
    const selectionBefore = [...state.selected];
    const before = myBase64;
    setSaving(true);
    try {
      // Si no tiene álbum, crear uno vacío primero
      if (!myBase64) {
        const empty = btoa(String.fromCharCode(...new Uint8Array(TOTAL)));
        await albumService.updateMyAlbum(empty);
      }
      const res = await albumService.addStickers(nums);
      const album = res.data;
      const after = album?.data ?? null;
      setMyBase64(after);
      dispatch({
        type: 'PUSH_UNDO',
        entry: {
          before,
          after,
          selectionBefore,
          label: t('Agregar {{n}} figuritas', { n: nums.length }),
        },
      });
      setToast({
        message: t('Agregaste {{n}} figuritas', { n: nums.length }),
        variant: 'success',
        actionLabel: t('Deshacer'),
        onAction: () => undoRef.current?.(),
      });
    } catch (e) {
      setToast({ message: t('No se pudo agregar las figuritas'), variant: 'error' });
    } finally {
      setSaving(false);
    }
  }, [state.selected, myBase64, t]);

  // ── Remove: -1 a cada figurita seleccionada (delta) ───────────────
  // Para confirmar la baja de figuritas sin repetidos, reusamos ConfirmDialog.
  const [removeQueue, setRemoveQueue] = useState(null); // { nums: [...], unique: number }

  const handleRemove = useCallback(() => {
    if (state.selected.size === 0) return;
    const notOwned = [];
    const noReps = [];
    for (const n of state.selected) {
      const s = myStickers.find(st => st.n === n);
      if (!s || !s.owned) notOwned.push(n);
      else if (s.reps < 2) noReps.push(n);
    }
    if (notOwned.length > 0 && notOwned.length === state.selected.size) {
      setToast({ message: t('Ninguna de las seleccionadas se puede eliminar'), variant: 'info' });
      return;
    }
    const nums = [...state.selected].filter(n => {
      const s = myStickers.find(st => st.n === n);
      return s && s.owned;
    });
    if (nums.length === 0) return;
    if (noReps.length > 0) {
      setRemoveQueue({ nums, unique: noReps.length });
      return;
    }
    runRemove(nums);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selected, myStickers, t]);

  const runRemove = useCallback(async (nums) => {
    // Tras eliminar: la selección se vacía y la acción queda en el undo stack
    // (incluye la selección original para restaurarla con Deshacer).
    const selectionBefore = [...state.selected];
    const before = myBase64;
    setSaving(true);
    try {
      const res = await albumService.removeStickers(nums);
      const album = res.data;
      const after = album?.data ?? null;
      setMyBase64(after);
      dispatch({
        type: 'PUSH_UNDO',
        entry: {
          before,
          after,
          selectionBefore,
          label: t('Eliminar {{n}} figuritas', { n: nums.length }),
        },
      });
      setToast({
        message: t('Eliminaste {{n}} figuritas', { n: nums.length }),
        variant: 'success',
        actionLabel: t('Deshacer'),
        onAction: () => undoRef.current?.(),
      });
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.data?.message
        || t('No se pudo eliminar las figuritas');
      setToast({ message: msg, variant: 'error' });
    } finally {
      setSaving(false);
      setRemoveQueue(null);
    }
  }, [state.selected, myBase64, t]);

  // Import desde figuritas.app: abre el modal con textarea + preview.
  const handleImport = useCallback(() => {
    setImportOpen(true);
  }, []);

  // Confirmación del modal de import: guarda el album entero (UPSERT).
  // Asentamos el estado actual como `before` del undo stack para poder
  // revertir si la persona quiso volver atrás.
  const handleImportConfirm = useCallback(async ({ base64, willSet, conflicts }) => {
    const before = myBase64;
    setSaving(true);
    try {
      const res = await albumService.updateMyAlbum(base64);
      const album = res.data;
      const after = album?.data ?? base64;
      setMyBase64(after);
      dispatch({
        type: 'PUSH_UNDO',
        entry: {
          before,
          after,
          selectionBefore: [],
          label: t('Importar de figuritas.app'),
        },
      });
      setImportOpen(false);
      const msg = conflicts > 0
        ? t('Importadas {{n}} figuritas. {{c}} pasaron a faltantes.', { n: willSet, c: conflicts })
        : t('Importadas {{n}} figuritas', { n: willSet });
      setToast({
        message: msg,
        variant: 'success',
        actionLabel: t('Deshacer'),
        onAction: () => undoRef.current?.(),
      });
    } catch (e) {
      setToast({ message: t('No se pudo importar el álbum'), variant: 'error' });
    } finally {
      setSaving(false);
    }
  }, [myBase64, t]);

  // ── Undo / Redo handlers ──────────────────────────────────────────
  const handleUndo = useCallback(async () => {
    const entry = state.undoStack[state.undoStack.length - 1];
    if (!entry) return;
    setSaving(true);
    try {
      // Restauramos el album al estado previo a la acción.
      const b64 = entry.before ?? btoa(String.fromCharCode(...new Uint8Array(TOTAL)));
      const res = await albumService.updateMyAlbum(b64);
      const album = res.data;
      setMyBase64(album?.data ?? b64);
      dispatch({ type: 'UNDO_POP' });
      setToast({ message: t('Acción deshecha'), variant: 'info' });
    } catch (e) {
      setToast({ message: t('No se pudo deshacer'), variant: 'error' });
    } finally {
      setSaving(false);
    }
  }, [state.undoStack, t]);

  const handleRedo = useCallback(async () => {
    const entry = state.redoStack[state.redoStack.length - 1];
    if (!entry) return;
    setSaving(true);
    try {
      const b64 = entry.after ?? btoa(String.fromCharCode(...new Uint8Array(TOTAL)));
      const res = await albumService.updateMyAlbum(b64);
      const album = res.data;
      setMyBase64(album?.data ?? b64);
      dispatch({ type: 'REDO_POP' });
      setToast({ message: t('Acción rehecha'), variant: 'info' });
    } catch (e) {
      setToast({ message: t('No se pudo rehacer'), variant: 'error' });
    } finally {
      setSaving(false);
    }
  }, [state.redoStack, t]);

  // Ref para que el toast pueda llamar al undo más reciente sin estar atado a closures
  const undoRef = useRef(null);
  useEffect(() => { undoRef.current = handleUndo; }, [handleUndo]);

  // Atajos de teclado: Ctrl/Cmd+Z = undo, Ctrl/Cmd+Shift+Z = redo (sólo en MY_ALBUM,
  // y sólo si no hay un input enfocado).
  useEffect(() => {
    if (state.viewMode !== 'MY_ALBUM') return;
    const onKey = (e) => {
      const tgt = e.target;
      const isInput = tgt && (tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA' || tgt.isContentEditable);
      if (isInput) return;
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (e.key === 'z' || e.key === 'Z') {
        if (e.shiftKey) {
          if (state.redoStack.length > 0) { e.preventDefault(); handleRedo(); }
        } else {
          if (state.undoStack.length > 0) { e.preventDefault(); handleUndo(); }
        }
      } else if (e.key === 'y' || e.key === 'Y') {
        if (state.redoStack.length > 0) { e.preventDefault(); handleRedo(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.viewMode, state.undoStack.length, state.redoStack.length, handleUndo, handleRedo]);


  // ── Quick-fill del builder (botón "Intercambio rápido" en bottom bar) ──
  // Misma lógica que SmartTradeCard.handleQuickSwap: spare∩theirNeeds y
  // theirSpare∩myNeeds, balanceado al min para que el trade sea parejo.
  const handleQuickFillTrade = useCallback(() => {
    const target = state.browsingUser;
    if (!target) return;
    const their = tradeIndex.get(target.id);
    if (!their || !myCounts) {
      setToast({ message: t('No hay un intercambio rápido posible con {{name}}', { name: target.name || '' }), variant: 'info' });
      return;
    }
    // Para spare descontamos locks (no puedo dar lo que ya me comprometí en
    // otro trade). Para needs, sumamos las que voy a recibir en trades
    // ACCEPTED — esas no son necesidad real porque ya las tengo "in flight".
    const effectiveForSpare = (lockedStickerMap && lockedStickerMap.size > 0)
      ? myCounts.map((c, i) => Math.max(0, c - (lockedStickerMap.get(i + 1) || 0)))
      : myCounts;
    const effectiveForNeeds = (incomingStickerMap && incomingStickerMap.size > 0)
      ? effectiveForSpare.map((c, i) => c + (incomingStickerMap.get(i + 1)?.length || 0))
      : effectiveForSpare;
    const mySpare = buildBitmapFromCounts(effectiveForSpare, c => c >= 2);
    const myNeeds = buildBitmapFromCounts(effectiveForNeeds, c => c === 0);
    const offeredAll = bitmapAndToList(mySpare, their.needs);
    const requestedAll = bitmapAndToList(their.spare, myNeeds);
    const swapSize = Math.min(offeredAll.length, requestedAll.length);
    if (swapSize === 0) {
      setToast({ message: t('No hay un intercambio rápido posible con {{name}}', { name: target.name || '' }), variant: 'info' });
      return;
    }
    dispatch({
      type: 'SET_TRADE_BUILDER',
      offering: offeredAll.slice(0, swapSize),
      requesting: requestedAll.slice(0, swapSize),
    });
  }, [state.browsingUser, tradeIndex, myCounts, lockedStickerMap, incomingStickerMap, t]);

  // ── Propose trade handler ──────────────────────────────────────────
  const handleProposeTrade = useCallback(async () => {
    if (!state.browsingUser) return;
    const offered = [...state.tradeBuilder.offering];
    const requested = [...state.tradeBuilder.requesting];
    if (offered.length === 0 && requested.length === 0) return;
    try {
      // Si venimos de "Contraofertar" sobre una propuesta previa, cancelarla
      // ahora — recién al confirmar la nueva. Si rejectTrade falla (la
      // propuesta ya no existe, race con el otro user, etc.) seguimos igual
      // con el propose para que la contraoferta llegue.
      if (pendingCounterOf != null) {
        try { await albumService.rejectTrade(pendingCounterOf); } catch (_) {}
      }
      await albumService.proposeTrade(state.browsingUser.id, offered, requested);
      setPendingCounterOf(null);
      await refreshTrades();
      setToast({
        message: t('Propuesta enviada a {{name}}', { name: state.browsingUser?.name || '' }),
        variant: 'success',
      });
      dispatch({ type: 'VIEW_MY_ALBUM' });
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.data?.message
        || t('No se pudo enviar la propuesta');
      setToast({ message: msg, variant: 'error' });
    }
  }, [state.browsingUser, state.tradeBuilder, refreshTrades, t, pendingCounterOf]);

  const handlePublishOffer = useCallback(async () => {
    const offered = [...state.tradeBuilder.offering];
    const requested = [...state.tradeBuilder.requesting];
    if (offered.length === 0 || requested.length === 0) return;
    try {
      await albumService.createOffer(offered, requested);
      setToast({ message: t('Oferta publicada'), variant: 'success' });
      dispatch({ type: 'VIEW_MY_ALBUM' });
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.data?.message
        || t('No se pudo crear la oferta');
      setToast({ message: msg, variant: 'error' });
    }
  }, [state.tradeBuilder, t]);

  // ── Trade lifecycle handlers ───────────────────────────────────────
  // Accept: ya no transfiere figuritas — sólo deja el trade reservado.
  // Tras aceptar, vamos derecho al TradeDetailModal así el usuario ve el
  // WhatsApp del contraparte sin tener que ir a buscarlo.
  const handleAcceptTrade = useCallback(async (tradeId) => {
    try {
      await albumService.acceptTrade(tradeId);
      // Construir el trade detail desde lo que ya teníamos en incomingTrades
      // (el id viene de IncomingTradesCard / TRADE_REVIEW de un incoming).
      const incoming = incomingTrades.find(t => t.id === tradeId);
      await refreshTrades();
      if (incoming) {
        dispatch({
          type: 'VIEW_TRADE_DETAIL',
          trade: {
            id: incoming.id,
            otherUser: tradeUserFromId(incoming.proposerId),
            youGive: incoming.requested || [],
            youReceive: incoming.offered || [],
            createdAt: incoming.createdAt,
            resolvedAt: new Date().toISOString(),
          },
        });
        setToast({
          message: t('Reservado — coordinen el intercambio por WhatsApp'),
          variant: 'success',
        });
      } else {
        // fallback: si por alguna razón no encontramos el incoming, volvemos al álbum
        dispatch({ type: 'VIEW_MY_ALBUM' });
        setToast({
          message: t('Reservado — coordiná por WhatsApp en Reservas activas'),
          variant: 'success',
        });
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.data?.message
        || t('No se pudo aceptar la propuesta');
      setToast({ message: msg, variant: 'error' });
    }
  }, [incomingTrades, refreshTrades, tradeUserFromId, t]);

  const handleRejectTrade = useCallback(async (tradeId) => {
    try {
      await albumService.rejectTrade(tradeId);
      await refreshTrades();
      setToast({ message: t('Propuesta rechazada'), variant: 'info' });
      dispatch({ type: 'VIEW_MY_ALBUM' });
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.data?.message
        || t('No se pudo rechazar la propuesta');
      setToast({ message: msg, variant: 'error' });
    }
  }, [refreshTrades, t]);

  // Complete y Cancel pasan por ConfirmDialog (montado a nivel de page).
  const handleCompleteTrade = useCallback((tradeId) => {
    setConfirm({ type: 'complete', tradeId });
  }, []);
  // ACCEPTED: deshacer la reserva, las figuritas vuelven a estar disponibles.
  const handleCancelAcceptedTrade = useCallback((tradeId) => {
    setConfirm({ type: 'cancel-accepted', tradeId });
  }, []);
  // PENDING (outgoing): cancelar mi propuesta antes de que el otro la acepte.
  const handleCancelPendingTrade = useCallback((tradeId) => {
    setConfirm({ type: 'cancel-pending', tradeId });
  }, []);

  // ── Banner action handlers (for TradeContextBanner) ──────────────────
  const closeBanner = useCallback(() => {
    setSearchParams({});
    setContextTrade(null);
    setContextOffer(null);
    dispatch({ type: 'VIEW_MY_ALBUM' });
  }, [setSearchParams]);

  // ── Escape: clear selection / cancel trade builder / close banner / leave view
  // Priority cascade so a single ESC does the most local "back out" action.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Don't hijack ESC while typing in inputs or while a confirm dialog is mounted.
      const target = e.target;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      if (confirm || removeQueue || valuationTarget !== null) return;

      if (state.selected.size > 0) {
        dispatch({ type: 'CLEAR_SELECTION' });
        e.preventDefault();
        return;
      }
      if (state.tradeBuilder.offering.size > 0 || state.tradeBuilder.requesting.size > 0) {
        dispatch({ type: 'CANCEL_TRADE' });
        e.preventDefault();
        return;
      }
      if (contextTrade || contextOffer) {
        closeBanner();
        e.preventDefault();
        return;
      }
      if (state.viewMode !== 'MY_ALBUM') {
        dispatch({ type: 'VIEW_MY_ALBUM' });
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.selected, state.tradeBuilder, state.viewMode, contextTrade, contextOffer, confirm, removeQueue, valuationTarget, closeBanner]);

  const handleBannerAccept = useCallback(async () => {
    if (!contextTrade) return;
    // handleAcceptTrade already calls refreshTrades + dispatches VIEW_TRADE_DETAIL.
    // After that the URL-param effect will re-resolve from mappedActive with ACCEPTED status.
    await handleAcceptTrade(contextTrade.id);
  }, [contextTrade, handleAcceptTrade]);

  const handleBannerReject = useCallback(async () => {
    if (!contextTrade) return;
    // handleRejectTrade calls refreshTrades + dispatches VIEW_MY_ALBUM.
    // We also clear the URL param so the banner disappears.
    await handleRejectTrade(contextTrade.id);
    setSearchParams({});
    setContextTrade(null);
  }, [contextTrade, handleRejectTrade, setSearchParams]);

  const handleBannerCancel = useCallback(() => {
    if (!contextTrade) return;
    if (contextTrade.status === 'ACCEPTED') {
      handleCancelAcceptedTrade(contextTrade.id);
    } else {
      handleCancelPendingTrade(contextTrade.id);
    }
  }, [contextTrade, handleCancelAcceptedTrade, handleCancelPendingTrade]);

  const handleBannerComplete = useCallback(() => {
    if (!contextTrade) return;
    handleCompleteTrade(contextTrade.id);
  }, [contextTrade, handleCompleteTrade]);

  const handleBannerContact = useCallback(async () => {
    if (!contextTrade) return;
    try {
      const res = await albumService.getTradeContact(contextTrade.id);
      const phone = res.data?.phone;
      const message = buildTradeMessage(t, {
        theirName: contextTrade.otherUser?.name,
        myName: myDisplayName,
        giveNums: contextTrade.youGive,
        getNums: contextTrade.youReceive,
      });
      const link = phone ? buildWhatsappLink(phone, message) : null;
      if (link) {
        window.open(link, '_blank', 'noopener,noreferrer');
        return;
      }
      setToast({ message: t('El otro usuario no tiene número de WhatsApp asociado'), variant: 'error' });
    } catch (e) {
      setToast({ message: t('Contacto no disponible en este momento.'), variant: 'error' });
    }
  }, [contextTrade, myDisplayName, t]);

  const handleBannerClaim = useCallback(async () => {
    if (!contextOffer) return;
    try {
      await albumService.claimOffer(contextOffer.id);
      setToast({ message: t('Oferta aceptada. Coordinen el intercambio.'), variant: 'success' });
      closeBanner();
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.data?.message || t('No se pudo reclamar la oferta');
      setToast({ message: msg, variant: 'error' });
    }
  }, [contextOffer, t, closeBanner]);

  const handleBannerCancelOffer = useCallback(async () => {
    if (!contextOffer) return;
    try {
      await albumService.cancelOffer(contextOffer.id);
      setToast({ message: t('Cancelar oferta') + ' — OK', variant: 'success' });
      closeBanner();
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.data?.message || t('No se pudo cancelar la oferta');
      setToast({ message: msg, variant: 'error' });
    }
  }, [contextOffer, t, closeBanner]);

  const runConfirm = useCallback(async () => {
    if (!confirm) return;
    setConfirm(c => c ? { ...c, busy: true } : null);
    try {
      if (confirm.type === 'complete') {
        await albumService.completeTrade(confirm.tradeId);
        const res = await albumService.getMyAlbum();
        setMyBase64(res.data?.data ?? null);
        setToast({ message: t('Intercambio completado'), variant: 'success' });
      } else if (confirm.type === 'cancel-accepted') {
        await albumService.cancelTrade(confirm.tradeId);
        setToast({ message: t('Reserva cancelada'), variant: 'success' });
      } else if (confirm.type === 'cancel-pending') {
        await albumService.cancelTrade(confirm.tradeId);
        setToast({ message: t('Propuesta cancelada'), variant: 'success' });
      }
      await refreshTrades();
      setConfirm(null);
      setSearchParams({});
      setContextTrade(null);
      dispatch({ type: 'VIEW_MY_ALBUM' });
    } catch (e) {
      const fallback = confirm.type === 'complete' ? t('No se pudo completar el intercambio')
        : confirm.type === 'cancel-pending' ? t('No se pudo cancelar la propuesta')
        : t('No se pudo cancelar la reserva');
      const msg = e.response?.data?.message || e.response?.data?.data?.message || fallback;
      setToast({ message: msg, variant: 'error' });
      setConfirm(null);
    }
  }, [confirm, refreshTrades, t]);

  if (loading) {
    return (
      <div className="dashboard-new font-body h-[100dvh] flex items-center justify-center" style={{ ...theme, backgroundColor: 'var(--chrome-bg)' }}>
        <p style={{ color: 'var(--chrome-muted)' }}>{t('Cargando álbum...')}</p>
      </div>
    );
  }

  return (
    <div
      className="dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex flex-col mobile-md:flex-row"
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      <DashboardSidebar
        activePath="/album"
        collapsed={state.sidebarCollapsed}
        onToggleCollapse={() => dispatch({ type: 'TOGGLE_SIDEBAR' })}
      />

      <main
        ref={mainRef}
        className={`${mainStyleExpanded ? '' : 'flex-1'} flex flex-col h-[100dvh] overflow-hidden`}
        style={{ background: albumTheme?.pageBg ?? C.surface, ...(mainStyleExpanded || {}) }}
      >
        <div className="shrink-0">
          <AlbumActionBar
            state={state}
            dispatch={dispatch}
            ownedCount={ownedCount}
            saving={saving}
            onImport={handleImport}
            onSetOwned={handleSetOwned}
            onRemove={handleRemove}
            onAcceptTrade={handleAcceptTrade}
            onRejectTrade={handleRejectTrade}
            onCancelOutgoing={handleCancelPendingTrade}
            onSendBuilderTrade={handleProposeTrade}
            onPublishOffer={handlePublishOffer}
            theme={albumTheme}
            onOpenPanel={() => setPanelOpen(true)}
            selectionMode={selectionMode}
            stickers={myStickers}
            onUndo={handleUndo}
            onRedo={handleRedo}
            canUndo={state.undoStack.length > 0}
            canRedo={state.redoStack.length > 0}
            undoLabel={state.undoStack[state.undoStack.length - 1]?.label}
            redoLabel={state.redoStack[state.redoStack.length - 1]?.label}
            onExport={() => setExportOpen(true)}
            chrome={chrome}
            chromeVariantName={CHROME_VARIANT_NAMES[chromeIdx]}
            onCycleChrome={() => {
              const next = (chromeIdx + 1) % CHROME_VARIANT_NAMES.length;
              setChromeIdx(next);
              setToast({
                message: t('Look: {{name}}', { name: CHROME_VARIANT_NAMES[next] }),
                variant: 'info',
              });
            }}
            onStickerAdded={handleStickerAdded}
            onNavigateToCountry={handleNavigateToCountry}
            mobileQuickAdd={mobileQuickAdd}
          />
          {/* Schedule summary visible en cualquier modo con browsingUser
              (USER_ALBUM, TRADE_MATCH, TRADE_REVIEW, etc.). Sólo se oculta
              en MY_ALBUM (donde no hay otra persona). */}
          {state.viewMode !== 'MY_ALBUM' && state.browsingUser?.id && (
            (() => {
              const entries = userSchedulesByUser[state.browsingUser.id];
              if (!entries || entries.length === 0) return null;
              return (
                <div className="px-4 mobile-md:px-6 pt-2">
                  <OtherUserScheduleSummary
                    entries={entries}
                    userId={state.browsingUser.id}
                    userName={state.browsingUser.name}
                  />
                </div>
              );
            })()
          )}
          {state.viewMode === 'MY_ALBUM' && showRepesTip && (
            <div
              className="hidden tablet-md:flex items-center gap-2 px-6 py-2 border-b"
              style={{
                backgroundColor: '#fffbeb',
                color: '#92400e',
                borderColor: '#fde68a',
              }}
            >
              <span className="material-symbols-outlined text-[16px] shrink-0" style={{ color: '#b45309' }}>tips_and_updates</span>
              <span className="text-[12px] leading-snug flex-1">
                <strong>{t('Tip')}:</strong> {t('si tocás una figurita que ya tenés sumás una repetida — ésas son las que después podés intercambiar.')}
              </span>
              <button
                type="button"
                onClick={dismissRepesTip}
                className="ml-1 hover:opacity-70 shrink-0 p-1 rounded"
                aria-label={t('Cerrar')}
                title={t('No mostrar de nuevo')}
              >
                <span className="material-symbols-outlined text-[16px]" style={{ color: '#92400e' }}>close</span>
              </button>
            </div>
          )}
          {state.viewMode === 'MY_ALBUM' && state.selected.size > 0 && (
            <div
              className="hidden tablet-sm:flex items-center gap-1.5 flex-wrap px-6 py-2 border-b"
              style={{
                backgroundColor: 'var(--md-sys-color-surface-container-low, rgba(0,0,0,0.02))',
                borderColor: 'var(--md-sys-color-outline-variant, rgba(0,0,0,0.10))',
              }}
            >
              <span className="text-[10px] uppercase tracking-wider font-bold text-on-surface-variant mr-1">
                {t('Seleccionadas')} ({state.selected.size})
              </span>
              {[...state.selected].sort((a, b) => a - b).map((n) => {
                let chipColor = chrome.accent;
                if (chrome.chipMode === 'country') {
                  const info = countryForSticker(n);
                  if (info?.color) chipColor = info.color;
                }
                const useGradient = chrome.gradient && chrome.chipMode !== 'country';
                return (
                  <span
                    key={n}
                    className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-bold tabular-nums border ${chrome.shimmer && useGradient ? 'cm-chrome-shimmer' : ''}`}
                    style={useGradient ? {
                      background: chrome.gradient,
                      backgroundSize: chrome.shimmer ? '200% 100%' : '100% 100%',
                      color: chrome.textOnGradient || '#3a2400',
                      borderColor: 'transparent',
                    } : {
                      backgroundColor: `${chipColor}26`,
                      color: chipColor,
                      borderColor: `${chipColor}55`,
                    }}
                  >
                    {stickerCode(n)}
                    <button
                      type="button"
                      onClick={() => dispatch({ type: 'TOGGLE_SELECT', n })}
                      className="hover:opacity-70 ml-0.5"
                      aria-label={t('Quitar')}
                    >
                      <span className="material-symbols-outlined text-[13px]">close</span>
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div
          ref={scrollContainerRef}
          className={`flex-1 overflow-y-auto hide-scrollbar tablet-sm:pb-0 ${
            state.viewMode === 'TRADE_MATCH' || state.viewMode === 'OFFER_BUILDER'
              ? 'pb-36'
              : state.selected.size > 0
                ? 'pb-36'
                : 'cm-pb-mobile-nav'
          }`}
        >
          {/* Context banner for ?tradeId / ?offerId URL params.
              Lo escondemos cuando el user "salió" del intercambio (volvió a
              MY_ALBUM): la URL todavía tiene tradeId/offerId pero el banner
              flotante quedaba como cartel fantasma — la interacción real con
              ese trade ya tiene su entry en el dashboard de la sidebar derecha. */}
          {(contextTrade || contextOffer) && state.viewMode !== 'MY_ALBUM' && (
            <TradeContextBanner
              trade={contextTrade}
              offer={contextOffer}
              myUserId={myUserId}
              myName={myDisplayName}
              onClose={closeBanner}
              onAccept={contextTrade?.status === 'PENDING' && contextTrade?.direction === 'incoming' ? handleBannerAccept : null}
              onReject={contextTrade?.status === 'PENDING' && contextTrade?.direction === 'incoming' ? handleBannerReject : null}
              onCancel={contextTrade ? handleBannerCancel : null}
              onComplete={contextTrade?.status === 'ACCEPTED' ? handleBannerComplete : null}
              onWhatsapp={contextTrade && ['ACCEPTED', 'COMPLETED'].includes(contextTrade.status) ? handleBannerContact : null}
              onClaim={contextOffer?.status === 'OPEN' && contextOffer?.userId !== myUserId ? handleBannerClaim : null}
              onCancelOffer={contextOffer?.status === 'OPEN' && contextOffer?.userId === myUserId ? handleBannerCancelOffer : null}
              theme={albumTheme}
            />
          )}

          {/* Empty state hint para nuevos usuarios */}
          {state.viewMode === 'MY_ALBUM' && ownedCount === 0 && !loading && (
            <div className="mx-3 mobile-md:mx-6 mt-4 mb-2 rounded-xl p-4 flex flex-col tablet-sm:flex-row tablet-sm:items-center gap-3"
                 style={{ backgroundColor: `${C.primary}11`, border: `1px dashed ${C.primary}55` }}>
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <span className="material-symbols-outlined text-2xl shrink-0" style={{ color: C.primary }}>touch_app</span>
                <div className="text-[12px]" style={{ color: C.onSurface }}>
                  <p className="font-bold mb-0.5">{t('Empezá tu álbum')}</p>
                  <p style={{ color: C.onSurfaceVariant }}>
                    {t('Tocá las figuritas que ya tenés para marcarlas como obtenidas. Apenas tengas algunas, te vamos a sugerir intercambios.')}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setImportOpen(true)}
                className="shrink-0 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all hover:shadow-md"
                style={{ backgroundColor: C.primary, color: '#fff' }}
              >
                <span className="material-symbols-outlined text-[18px]">file_download</span>
                {t('Importar de figuritas.app')}
              </button>
            </div>
          )}

          {/* Pending incoming trades banner — solo en MY_ALBUM, arriba de la grilla.
              Toda la fila es un <button> real para que el click funcione sin
              ambigüedades de event bubbling. Con 1 propuesta abre ese trade
              directo (mismo flow que clickear la fila del dashboard); con
              varias va al tab Recibidas para que el user elija cuál responder. */}
          {state.viewMode === 'MY_ALBUM' && incomingTrades.length > 0 && (
            <button
              type="button"
              onClick={() => {
                if (incomingTrades.length === 1) {
                  navigate(`/album?tradeId=${incomingTrades[0].id}`);
                } else {
                  navigate('/album/dashboard?tab=Recibidas');
                }
              }}
              className="w-[calc(100%-1.5rem)] mobile-md:w-[calc(100%-3rem)] mx-3 mobile-md:mx-6 mt-4 mb-2 rounded-xl p-3 mobile-md:p-4 flex items-center gap-3 bg-surface-container border border-outline shadow-sm cursor-pointer hover:bg-surface-container-high transition-colors text-left"
            >
              <span className="material-symbols-outlined text-2xl shrink-0 text-primary">notifications_active</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-on-surface">
                  {t('Tenés {{n}} propuestas para responder', { n: incomingTrades.length })}
                </p>
                <p className="text-[11px] text-on-surface-variant">
                  {incomingTrades.length === 1
                    ? t('Tocá para ver la propuesta.')
                    : t('Aceptá o rechazá las que te sirvan desde el dashboard.')}
                </p>
              </div>
              <span
                className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold text-white"
                style={{ backgroundColor: 'var(--md-sys-color-primary, #2563eb)' }}
              >
                {incomingTrades.length === 1 ? t('Ver propuesta') : t('Ver propuestas')}
              </span>
            </button>
          )}
          {isMobile ? (
            <MobileAlbumGrid
              stickers={gridStickers}
              viewMode={state.viewMode}
              selected={state.selected}
              hideRepeated={false}
              extras={extras}
              onCellClick={handleCellClick}
              theme={albumTheme}
              layout={mobileLayoutId}
              participatingStickers={participatingStickerSet}
              searchQuery=""
            />
          ) : (
            <AlbumGrid
              stickers={gridStickers}
              viewMode={state.viewMode}
              selected={state.selected}
              hideRepeated={false}
              extras={extras}
              onCellClick={handleCellClick}
              columns={state.gridColumns}
              theme={albumTheme}
              layout={effectiveLayoutId}
              participatingStickers={participatingStickerSet}
            />
          )}

          {/* Botón Exportar progreso al final del scroll en /album/5 mobile.
              Vive inline (no FAB flotante) — aparece justo debajo de la
              última card. */}
          {isMobile && mobileQuickAdd && state.viewMode === 'MY_ALBUM' && (
            <div className="mobile-md:hidden flex justify-end px-4 pt-3 pb-6">
              <button
                type="button"
                onClick={() => setExportOpen(true)}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-full font-bold text-xs transition-opacity"
                style={{ backgroundColor: C.primary, color: '#fff', boxShadow: `0 4px 12px -4px ${C.primary}` }}
              >
                <span className="material-symbols-outlined text-base">file_download</span>
                {t('Exportar progreso')}
              </button>
            </div>
          )}
        </div>
      </main>

      <RightPanel
        dispatch={dispatch}
        myCounts={myCounts}
        selected={state.selected}
        viewMode={state.viewMode}
        allUsers={allUsers}
        tradeIndex={tradeIndex}
        onViewUser={(user, pendingSelection, opts) => viewUserAlbum(user, pendingSelection, { autoEnterMatch: true, ...(opts || {}) })}
        onQuickSwap={(user, offered, requested) => {
          const sel = new Set([...(offered || []), ...(requested || [])]);
          viewUserAlbum(user, sel, { autoEnterMatch: true });
        }}
        onCreateOffer={() => dispatch({ type: 'VIEW_OFFER_BUILDER' })}
        lockedSet={lockedStickerMap}
        incomingSet={incomingStickerMap}
        myCommunities={myCommunities}
        selectedCommunityIds={selectedCommunityIds}
        onToggleCommunity={toggleCommunity}
        mobileOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        selectionMode={selectionMode}
        onToast={(message, variant) => setToast({ message, variant })}
        rightbarVariant={rightbarIdx}
        dashboardVariant={dashboardIdx}
        isMobile={isMobile}
      />

      <ConfirmDialog
        open={!!confirm}
        busy={!!confirm?.busy}
        variant={confirm?.type === 'complete' ? 'primary' : 'danger'}
        title={
          confirm?.type === 'complete' ? t('Confirmar intercambio')
          : confirm?.type === 'cancel-pending' ? t('Cancelar propuesta')
          : t('Cancelar reserva')
        }
        body={
          confirm?.type === 'complete'
            ? t('¿Ya intercambiaron las figuritas en persona? Esto las moverá entre los álbumes y no se puede deshacer.')
          : confirm?.type === 'cancel-pending'
            ? t('La propuesta se va a cancelar y el otro usuario no podrá aceptarla.')
          : t('Las figuritas vuelven a estar disponibles para ambos. ¿Confirmás?')
        }
        confirmLabel={
          confirm?.type === 'complete' ? t('Sí, completar')
          : confirm?.type === 'cancel-pending' ? t('Sí, cancelar')
          : t('Sí, cancelar')
        }
        cancelLabel={confirm?.type === 'complete' ? t('No, todavía no') : t('No')}
        onConfirm={runConfirm}
        onCancel={() => setConfirm(null)}
        theme={albumTheme}
      />

      <ConfirmDialog
        open={!!removeQueue}
        variant="danger"
        title={t('Eliminar figuritas')}
        body={removeQueue?.unique === 1
          ? t('1 figurita no tiene duplicados — al eliminarla quedará marcada como no obtenida. ¿Continuás?')
          : t('{{n}} figuritas no tienen duplicados — al eliminarlas quedarán marcadas como no obtenidas. ¿Continuás?', { n: removeQueue?.unique || 0 })}
        confirmLabel={t('Sí, eliminar')}
        cancelLabel={t('No')}
        onConfirm={() => removeQueue && runRemove(removeQueue.nums)}
        onCancel={() => setRemoveQueue(null)}
        theme={albumTheme}
      />

      <ExportAlbumDialog
        open={exportOpen}
        stickers={myStickers}
        displayName={myDisplayName}
        onClose={() => setExportOpen(false)}
        onToast={(payload) => setToast(payload)}
      />

      <ImportAlbumDialog
        open={importOpen}
        currentBase64={myBase64}
        onClose={() => setImportOpen(false)}
        onConfirm={handleImportConfirm}
        onToast={(payload) => setToast(payload)}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />

      {valuationTarget !== null && (
        <ValuationPicker
          stickerNum={valuationTarget}
          currentValue={myValuations?.[valuationTarget] ?? 1}
          onPick={(v) => handleSetValuation(valuationTarget, v)}
          onClose={() => setValuationTarget(null)}
        />
      )}

      {/* Floating variant selectors (theme + layout) — dev-only, dismissable */}
      {isDevMode() && showSelectors && (
        <div className="fixed bottom-20 tablet-sm:bottom-6 right-4 z-[100] hidden tablet-md:flex flex-col items-end gap-2">
          <div className="flex items-center gap-2 bg-white rounded-full shadow-lg border border-gray-200 px-3 py-2">
            <button
              onClick={() => setShowSelectors(false)}
              className="hover:bg-gray-100 rounded-full p-0.5 transition-colors"
              aria-label="Close selectors"
              title="Hide"
            >
              <span className="material-symbols-outlined text-sm text-gray-500">close</span>
            </button>
            <span className="text-[9px] font-black uppercase tracking-widest text-gray-500 mr-1">Theme</span>
            <button onClick={() => setBarVariant(v => (v - 1 + ALBUM_VARIANT_NAMES.length) % ALBUM_VARIANT_NAMES.length)} className="hover:bg-gray-100 rounded-full p-0.5 transition-colors">
              <span className="material-symbols-outlined text-sm text-gray-700">chevron_left</span>
            </button>
            <span className="text-xs font-bold text-gray-800 min-w-[260px] text-center">
              {barVariant}: {ALBUM_VARIANT_NAMES[barVariant]}
            </span>
            <button onClick={() => setBarVariant(v => (v + 1) % ALBUM_VARIANT_NAMES.length)} className="hover:bg-gray-100 rounded-full p-0.5 transition-colors">
              <span className="material-symbols-outlined text-sm text-gray-700">chevron_right</span>
            </button>
          </div>
          <div className="flex items-center gap-2 bg-white rounded-full shadow-lg border border-gray-200 px-3 py-2">
            <span className="text-[9px] font-black uppercase tracking-widest text-gray-500 mr-1">Layout</span>
            <button onClick={() => setLayoutIdx(v => (v - 1 + availableLayouts.length) % availableLayouts.length)} className="hover:bg-gray-100 rounded-full p-0.5 transition-colors">
              <span className="material-symbols-outlined text-sm text-gray-700">chevron_left</span>
            </button>
            <span className="text-xs font-bold text-gray-800 min-w-[230px] text-center">
              {layoutIdx}: {t(currentLayout.labelKey)}
            </span>
            <button onClick={() => setLayoutIdx(v => (v + 1) % availableLayouts.length)} className="hover:bg-gray-100 rounded-full p-0.5 transition-colors">
              <span className="material-symbols-outlined text-sm text-gray-700">chevron_right</span>
            </button>
          </div>
          <div className="flex items-center gap-2 bg-white rounded-full shadow-lg border border-gray-200 px-3 py-2">
            <span className="text-[9px] font-black uppercase tracking-widest text-gray-500 mr-1">Rightbar</span>
            <button onClick={() => setRightbarIdx(v => (v - 1 + RIGHTBAR_VARIANT_NAMES.length) % RIGHTBAR_VARIANT_NAMES.length)} className="hover:bg-gray-100 rounded-full p-0.5 transition-colors">
              <span className="material-symbols-outlined text-sm text-gray-700">chevron_left</span>
            </button>
            <span className="text-xs font-bold text-gray-800 min-w-[180px] text-center">
              {rightbarIdx}: {RIGHTBAR_VARIANT_NAMES[rightbarIdx]}
            </span>
            <button onClick={() => setRightbarIdx(v => (v + 1) % RIGHTBAR_VARIANT_NAMES.length)} className="hover:bg-gray-100 rounded-full p-0.5 transition-colors">
              <span className="material-symbols-outlined text-sm text-gray-700">chevron_right</span>
            </button>
          </div>
          <div className="flex items-center gap-2 bg-white rounded-full shadow-lg border border-gray-200 px-3 py-2">
            <span className="text-[9px] font-black uppercase tracking-widest text-gray-500 mr-1">Dashboard</span>
            <button onClick={() => setDashboardIdx(v => (v - 1 + DASHBOARD_VARIANT_NAMES.length) % DASHBOARD_VARIANT_NAMES.length)} className="hover:bg-gray-100 rounded-full p-0.5 transition-colors">
              <span className="material-symbols-outlined text-sm text-gray-700">chevron_left</span>
            </button>
            <span className="text-xs font-bold text-gray-800 min-w-[200px] text-center">
              {dashboardIdx}: {DASHBOARD_VARIANT_NAMES[dashboardIdx]}
            </span>
            <button onClick={() => setDashboardIdx(v => (v + 1) % DASHBOARD_VARIANT_NAMES.length)} className="hover:bg-gray-100 rounded-full p-0.5 transition-colors">
              <span className="material-symbols-outlined text-sm text-gray-700">chevron_right</span>
            </button>
          </div>
          <div className="flex items-center gap-2 bg-white rounded-full shadow-lg border border-gray-200 px-3 py-2">
            <span className="text-[9px] font-black uppercase tracking-widest text-gray-500 mr-1">Chrome</span>
            <button onClick={() => setChromeIdx(v => (v - 1 + CHROME_VARIANT_NAMES.length) % CHROME_VARIANT_NAMES.length)} className="hover:bg-gray-100 rounded-full p-0.5 transition-colors">
              <span className="material-symbols-outlined text-sm text-gray-700">chevron_left</span>
            </button>
            <span className="text-xs font-bold text-gray-800 min-w-[210px] text-center">
              {chromeIdx}: {CHROME_VARIANT_NAMES[chromeIdx]}
            </span>
            <button onClick={() => setChromeIdx(v => (v + 1) % CHROME_VARIANT_NAMES.length)} className="hover:bg-gray-100 rounded-full p-0.5 transition-colors">
              <span className="material-symbols-outlined text-sm text-gray-700">chevron_right</span>
            </button>
          </div>
        </div>
      )}
      {isDevMode() && !showSelectors && (
        <button
          onClick={() => setShowSelectors(true)}
          className="fixed bottom-20 tablet-sm:bottom-6 right-4 z-[100] hidden tablet-md:flex w-10 h-10 items-center justify-center bg-white rounded-full shadow-lg border border-gray-200 hover:bg-gray-100 transition-colors"
          aria-label="Show theme & layout selectors"
          title="Theme & Layout"
        >
          <span className="material-symbols-outlined text-base text-gray-600">tune</span>
        </button>
      )}

      {state.viewMode === 'TRADE_MATCH' && (
        <TradeSummaryBar
          offering={state.tradeBuilder.offering}
          requesting={state.tradeBuilder.requesting}
          onReview={() => dispatch({ type: 'VIEW_TRADE_REVIEW', trade: {
            id: 'builder',
            fromUser: state.browsingUser,
            stickersOffered: [...state.tradeBuilder.offering],
            stickersRequested: [...state.tradeBuilder.requesting],
          } })}
          onClear={() => dispatch({ type: 'CANCEL_TRADE' })}
          onSend={handleProposeTrade}
          onQuickFill={handleQuickFillTrade}
          theme={albumTheme}
        />
      )}

      {state.viewMode === 'OFFER_BUILDER' && (
        <TradeSummaryBar
          offering={state.tradeBuilder.offering}
          requesting={state.tradeBuilder.requesting}
          onReview={() => dispatch({ type: 'VIEW_TRADE_REVIEW', trade: {
            id: 'offer-builder',
            stickersOffered: [...state.tradeBuilder.offering],
            stickersRequested: [...state.tradeBuilder.requesting],
            youGive: [...state.tradeBuilder.offering],
            youReceive: [...state.tradeBuilder.requesting],
          } })}
          onClear={() => dispatch({ type: 'CANCEL_TRADE' })}
          onSend={handlePublishOffer}
          theme={albumTheme}
        />
      )}
    </div>
  );
}

export default StickerAlbum;
